import apiClient from '../../../Services/ApiClient';
import {
	Command,
	ExecutedCommand,
	FinishedCommand,
	RunableCommand,
} from '../Commands/Command';
import { useEffect } from 'react';
import { useAppContext } from '../../../Services/AppContext';
import useNotification from '../../../Hooks/useNotification.hook';
import { useNCZICommandContext } from '../Services/NCZICommandContext';
import { v4 as uuidv4 } from 'uuid';

interface NcziCommandChange {
	state: string;
	messageId: string;
	error: string;
}

interface useNCZICommandsProps {
	hideError?: boolean;
}

export default function useNCZICommands(props?: useNCZICommandsProps) {
	const { hideError = false } = props ?? {};
	const { addCommand, updateCommand, commands } = useNCZICommandContext();

	const { socket } = useAppContext();
	const { showNotificationAlert } = useNotification();

	const handleSocketChange = async (data: NcziCommandChange) => {
		const command = commands[data.messageId];
		if (command) {
			if (data.error && data.error.length) {
				if (
					!hideError &&
					data.error.indexOf('E000000079') === -1 &&
					data.error.indexOf('CHYBA #5001') === -1 &&
					data.error.indexOf('neexistujú dáta pre vytvorenie výstupu') === -1
				) {
					showNotificationAlert({ content: data.error });
				}
				const changedCommand: FinishedCommand = {
					...command,
					state: data.state,
					error: true,
					errorMessage: data.error,
				};
				updateCommand(changedCommand);
			} else if (data.state === 'done') {
				const { data: response } = await apiClient.get(
					'/nczi/message/' + data.messageId + '/response',
				);
				if (response.data) {
					const changedCommand: FinishedCommand = {
						...command,
						state: data.state,
						error: false,
						errorMessage: data.error,
						result: response.data,
					};
					updateCommand(changedCommand);
				} else {
					setTimeout(async () => {
						const { data: response } = await apiClient.get(
							'/nczi/message/' + data.messageId + '/response',
						);
						const changedCommand: FinishedCommand = {
							...command,
							state: data.state,
							error: false,
							errorMessage: data.error,
							result: response.data,
						};
						updateCommand(changedCommand);
					}, 1000);
				}
			} else {
				const changedCommand: RunableCommand = {
					...command,
					state: data.state,
				};
				updateCommand(changedCommand);
			}
		}
	};

	useEffect(() => {
		socket.on('ncziMessageState', handleSocketChange);
		return () => {
			socket.off('ncziMessageState', handleSocketChange);
		};
	}, [commands]);

	const execute = async (
		command: Command,
	): Promise<ExecutedCommand | FinishedCommand> => {
		const isPatientIdCommand =
			command.operation === 'DajJRUZIdentifikator_GW_v2';
		const commandId = uuidv4();

		const executingCommand = { id: commandId, state: 'executing', ...command };
		addCommand(executingCommand);

		const { data } = isPatientIdCommand
			? await apiClient.post('/nczi/get-patient-code', {
					...command.params,
					id: commandId,
			  }) // lze zaslat i na execute, ale tahle drzi pamet (skipped state)
			: await apiClient.post('/nczi/execute', executingCommand);

		// if(data.error && data.statusCode) {
		// 	throw Error('Request error');
		// }

		if (data.error) {
			const finishedCommand = {
				...command,
				id: data.id,
				state: 'error',
				error: true,
				errorMessage: data.message,
			};

			showNotificationAlert({ content: data.message });

			updateCommand(finishedCommand);
			return finishedCommand;
		}

		if (data.state === 'skipped') {
			const finishedCommand = {
				...command,
				id: commandId,
				state: 'done',
				error: false,
				result: {
					patientCode: data.resultId, // skipped is only in GetPatientCode
				},
			};

			updateCommand(finishedCommand);
			return finishedCommand;
		}

		if (data.offline === true || data.manual === true) {
			const finishedCommand = {
				...command,
				id: commandId,
				state: data.offline === true ? 'offline' : 'manual',
				error: false,
			};

			updateCommand(finishedCommand);
			return finishedCommand;
		}

		const executedCommand = {
			...command,
			id: data.id ?? null,
			state: data.state,
		};

		updateCommand(executedCommand);
		return executedCommand;
	};

	return { commands, execute };
}
